import { PageContainer, PageHeader, Spread, Text } from '@/components/Atoms'
import { ExportCsvMenu, ExportCsvMenuItem } from '@/components/ExportCsv/ExportCsvMenu'
import { Spinner } from '@/components/Spinner'
import {
  productTypeVariantDetailsExportQueryOptions,
  productTypeVariantDetailsRawExportQueryOptions,
  productTypeVariantDetailsSearchSchema,
  useProductTypeVariants,
} from '@/hooks/queries/crt/useProductTypeVariant'
import { useTablePagination } from '@/hooks/useTablePagination'
import { queryClient } from '@/main'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import ProductTypeVariantsDataTable from './-components/ProductTypeVariantsDataTable'

export const Route = createFileRoute(
  '/_auth/_modules/crt/materials/insights/product-type-variants/',
)({
  component: RouteComponent,
  validateSearch: (search) => productTypeVariantDetailsSearchSchema.parse(search),
})

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const { data, isPending, isError } = useProductTypeVariants({
    ...search,
    page: search.page ?? 1,
    pageSize,
  })

  if (isPending) {
    return (
      <PageContainer className="flex items-center justify-center">
        <Spinner size="lg" />
      </PageContainer>
    )
  }

  if (isError || !data) {
    return (
      <PageContainer>
        <Text>{t('errors.productTypeVariant.fetchFailed')}</Text>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <Spread>
        <PageHeader>{t('productTypeVariant.pageHeader')}</PageHeader>
        <ExportCsvMenu
          items={[
            <ExportCsvMenuItem
              label={t('export.viewData', { ns: 'common' })}
              description={t('export.viewDataDescription', { ns: 'common' })}
              getCsvExport={() =>
                queryClient.ensureQueryData(productTypeVariantDetailsExportQueryOptions(search))
              }
              rowCount={data.rowCount}
            />,
            <ExportCsvMenuItem
              label={t('export.rawData', { ns: 'common' })}
              description={t('export.rawDataDescription', { ns: 'common' })}
              getCsvExport={() =>
                queryClient.ensureQueryData(productTypeVariantDetailsRawExportQueryOptions)
              }
              rowCount={data.rawRowCount}
            />,
          ]}
        />
      </Spread>

      <ProductTypeVariantsDataTable
        data={data.results}
        rowCount={data.rowCount}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />
    </PageContainer>
  )
}
